﻿import { useWorkOrderModalsContext } from "@/shared/work-orders-modals/context/work-order-modals.context";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Grid2 from "@mui/material/Grid2";
import IconButton from "@mui/material/IconButton";
import { createStyles, makeStyles } from "@mui/styles";
import AddBoxIcon from "@mui/icons-material/AddBox";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import FormatListBulletedSharpIcon from "@mui/icons-material/FormatListBulletedSharp";
import CustomLoadingOverlay from "../../../../../../components/loaders/LazyLoading/LazyLoading";
import type {
	ColDef,
	DefaultMenuItem,
	EditableCallbackParams,
	GetContextMenuItemsParams,
	GridApi,
	IContextMenuParams,
	MenuItemDef,
	RowClassParams,
	RowNode,
	SelectionChangedEvent,
} from "ag-grid-community";
import "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { WorkOrderAttachment } from "../../models/WorkOrderAttachment";
import { ErrorModal, useToken } from "@dexteel/mesf-core";
import { useWorkOrderAttachmentGridActionsAndButtons } from "./hooks/useWorkOrderAttachmentsGridActionsAndButtons";
import { AttachmentNameColumn } from "./components/editing-row/attachment-name.column";
import { AttachmentsDownloadButtons } from "./components/download-buttons/attachment-download-buttons.columns";
import { AssetAddAttachmentDialog } from "../asset-resources-dialogs/attachments-resource/asset-add-attachments-dialog";
import { UploadFilePage } from "./components/uploadFile-dialog/uploadFilePage";
import { useSearchWorkOrderAttachments } from "./hooks/useSearchWorkOrderAttachments";
import { PreviewerAttachment } from "./components/viewer-attachments/previewer-attachment";
import { globalDefaultColDef } from "@setup/ag-grid/mesf-theme";

const useStyles = makeStyles((theme) =>
	createStyles({
		agGridContainer: {
			position: "relative",
		},
		btnAdd: {
			position: "absolute",
			padding: 0,
			bottom: 5.5,
			right: 25,
		},
		btnDelete: {
			position: "absolute",
			padding: 0,
			bottom: 7,
			right: 63,
		},
		btnCached: {
			position: "absolute",
			padding: 0,
			bottom: 8,
			left: 5,
		},
	}),
);

type Props = {
	WorkOrderId: number | null;
	newMode?: boolean;
	AssetId: number | null;
	searchAssetResources: Function;
	show: boolean;
	onHide: (shouldUpdate: boolean) => void;
	setShowAssetAttachmentsPicker: Function;
	isLoadingResourcesList: boolean;
	WorkOrderName: string;
	AssetName: string;
};

export const WorkOrderAttachmentsTable = ({
	WorkOrderId,
	newMode,
	AssetId,
	searchAssetResources,
	show,
	isLoadingResourcesList,
	WorkOrderName,
	AssetName,
}: Props) => {
	const classes = useStyles();
	const [error, setError] = useState("");
	const [errorApi, setErrorApi] = useState("");
	const isMountRef = useRef(true);
	const { token: bearerToken } = useToken();

	const [isWorkOrderAttachmentsLoading, setIsWorkOrderAttachmentsLoading] =
		useState<boolean>(false);
	const [workOrderAttachmentsError, setWorkOrderAttachmentsError] =
		useState<string>("");

	const [linkAttachmentError, setLinkAttachmentError] = useState<string>("");
	const [removeAttachmentError, setRemoveAttachmentError] =
		useState<string>("");

	const [showLinkResourcesToAssetDialog, setShowLinkResourcesToAssetDialog] =
		useState<boolean>(false);

	const [listMessage, setListMessage] = useState<string>("");
	const [localShowInheritMessage, setLocalShowInheritMessage] = useState(false);
	const [newUploadedAttachments, setNewUploadedAttachments] = useState<
		WorkOrderAttachment[]
	>([]);

	const {
		state: {
			assetAttachments,
			workOrderAttachmentsList,
			rowSelectedInAttachment,
			uploadFileDialog,
			displayViewerAttachment,
		},
		actions: {
			setRowSelectedInAttachment,
			setDisplayViewerAttachment,
			setUploadFileDialog,
		},
	} = useWorkOrderModalsContext();

	const [gridApi, setGridApi] = useState<GridApi | null>(null);
	const containerStyle = useMemo(() => ({ height: "100%", width: "100%" }), []);
	const gridStyle = useMemo(() => ({ height: "100%", width: "100%" }), []);
	const [listAttachmentSelected, setListAttachmentSelected] = useState<any[]>(
		[],
	);
	const [columnDefs, setColumnDefs] = useState<ColDef[]>();

	const searchWorkOrderAttachment = useSearchWorkOrderAttachments({
		WorkOrderId,
		setIsWorkOrderAttachmentsLoading,
		setWorkOrderAttachmentsError,
	});

	const loadingOverlayComponent = useMemo<any>(() => {
		return CustomLoadingOverlay;
	}, []);

	const onActionsClick = (
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
		params: EditableCallbackParams,
	) => {
		const contextMenuParams: IContextMenuParams = {
			rowNode: params.node,
			column: params.column,
			value: params.node?.data,
			x: e.nativeEvent.clientX,
			y: e.nativeEvent.clientY,
			source: "ui",
		};
		params.api.showContextMenu(contextMenuParams);
	};

	const getRowStyle = useCallback(
		(params: RowClassParams): { [key: string]: string } | undefined => {
			if (params.data.IsAssetResource) {
				return { backgroundColor: "#e8f5e9" };
			}
		},
		[],
	);

	const {
		refresh,
		removeAttachment,
		deleteButton,
		saveButton,
		deleteOpen,
		setSaveButton,
		setDiscardButton,
		setAddButton,
		setDeleteButton,
		setDeleteOpen,
	} = useWorkOrderAttachmentGridActionsAndButtons({
		WorkOrderId,
		newMode,
		setShowLinkResourcesToAssetDialog,
		gridRef: gridApi,
		searchWorkOrderAttachment,
		setRemoveAttachmentError,
		setListMessage,
	});

	const renderColumns = () => {
		setColumnDefs([]);
		const newColumns: ColDef[] = [];
		newColumns.push({
			field: "",
			flex: 1,
			minWidth: 33,
			maxWidth: 33,
			filter: false,
			headerCheckboxSelection: true,
			checkboxSelection: true,
			headerCheckboxSelectionFilteredOnly: true,
		});
		newColumns.push({
			cellRenderer: AttachmentNameColumn,
			field: "FileName",
			flex: 3,
			minWidth: 150,
			type: "columnProperties",
		});
		newColumns.push({
			cellRenderer: AttachmentsDownloadButtons,
			field: "Download Attachment",
			flex: 1,
			minWidth: 130,
			type: "columnProperties",
		});
		newColumns.push({
			headerName: "",
			flex: 1,
			minWidth: 65,
			maxWidth: 65,
			cellClass: "ag-columns",
			wrapText: false,
			autoHeight: false,
			cellRenderer: (params: EditableCallbackParams) => {
				return (
					<Grid2
						container
						style={{
							height: "100%",
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
						}}
					>
						<Grid2
							style={{
								display: "flex",
								justifyContent: "center",
								alignItems: "center",
							}}
							size={{ xs: 12, md: 12 }}
						>
							<Grid2
								container
								spacing={1}
								style={{ justifyContent: "flex-end", minWidth: 30 }}
							>
								<Grid2 size={{ xs: 12, md: 12 }}>
									<Button
										fullWidth
										style={{}}
										onClick={(e) => onActionsClick(e, params)}
									>
										<FormatListBulletedSharpIcon
											style={{ height: "auto" }}
											color="action"
										/>
									</Button>
								</Grid2>
							</Grid2>
						</Grid2>
					</Grid2>
				);
			},
		});
		setColumnDefs(newColumns);
	};

	const getContextMenuItems = useCallback(
		(params: GetContextMenuItemsParams): (DefaultMenuItem | MenuItemDef)[] => {
			setRowSelectedInAttachment(params.node?.data);
			const rowSelectedInGrid = params.node?.data;

			const DownloaAttachment = () => {
				const link = document.createElement("a");
				link.href = `/backend/files/${params.node?.data.UploadFileId}/download?Authorization=${bearerToken}`;
				link.setAttribute("target", `_blank}`);
				link.setAttribute(
					"download",
					`${params.node?.data.FileName.replace(/\s+/g, "")}`,
				);
				document.body.appendChild(link);
				link.click();
			};

			const result: (DefaultMenuItem | MenuItemDef)[] = [];

			if (params.node?.data) {
				result.push({
					name: "View Attachment</strong>",
					action: () => {
						setRowSelectedInAttachment(params.node?.data);
						setDisplayViewerAttachment(true);
					},
					icon: '<i class="fas fa-file-invoice"></i>',
				});
			}

			if (saveButton) {
				result.push({
					name: "Save ",
					action: () => {},
					icon: '<i class="fas fa-save"></i>',
				});
			}

			if (params.node?.data) {
				result.push({
					name: "Remove Attachment",
					action: () => {
						setDeleteOpen(true);
					},
					icon: '<i class="fas fa-trash"></i>',
				});
			}

			if (rowSelectedInGrid) {
				if (params.node?.data.Path) {
					result.push({
						name: "Download Attachment",
						action: DownloaAttachment,
						icon: '<i class="far fa-file-alt"></i>',
					});
				}
			}

			result.push({
				name: "Add Attachment",
				action: () => setUploadFileDialog(true),
				icon: '<i class="fas fa-plus"></i>',
			});
			return result;
		},
		[deleteButton, saveButton, WorkOrderId, bearerToken],
	);

	const onSelectionChanged = useCallback(
		(event: SelectionChangedEvent) => {
			const selectedRows = event.api.getSelectedRows();
			setListAttachmentSelected(selectedRows);
		},
		[setListAttachmentSelected],
	);

	const openModalViewerActive = () => {
		const selectedRow: RowNode<any>[] | any = gridApi?.getSelectedNodes();
		if (selectedRow[0]!.data !== undefined) {
			setRowSelectedInAttachment(selectedRow[0]!.data);
			setDisplayViewerAttachment(!displayViewerAttachment);
		}
	};

	useEffect(() => {
		setSaveButton(false);
		setDiscardButton(false);
		setAddButton(true);
		setDeleteButton(false);
		setDeleteOpen(false);
	}, [WorkOrderId]);

	useEffect(() => {
		renderColumns();
	}, []);

	useEffect(() => {
		const hasAssetResource = workOrderAttachmentsList.some(
			(Attachment: WorkOrderAttachment) => Attachment.IsAssetResource === true,
		);
		const shouldShowInheritMessage =
			(!hasAssetResource || workOrderAttachmentsList.length === 0) &&
			assetAttachments.length > 0;

		if (show && newMode && shouldShowInheritMessage && isMountRef.current) {
			setLocalShowInheritMessage(true);
			isMountRef.current = false;
		} else if (!show) {
			setLocalShowInheritMessage(false);
		}
	}, [assetAttachments, show]);

	return (
		<>
			{show && (
				<div style={containerStyle}>
					<Grid2 container style={gridStyle}>
						<Grid2
							style={{ paddingBottom: 45 }}
							className={classes.agGridContainer}
							size={{ md: 12, xs: 12 }}
						>
							<AgGridReact
								onGridReady={(params) => setGridApi(params.api)}
								rowData={workOrderAttachmentsList}
								columnDefs={columnDefs}
								defaultColDef={globalDefaultColDef}
								headerHeight={32}
								animateRows={true}
								getContextMenuItems={getContextMenuItems}
								rowSelection="multiple"
								onSelectionChanged={onSelectionChanged}
								loadingOverlayComponent={loadingOverlayComponent}
								onRowDoubleClicked={openModalViewerActive}
								getRowStyle={getRowStyle}
								overlayNoRowsTemplate={"No Attachment added"}
								loading={isWorkOrderAttachmentsLoading}
							/>
							<>
								<IconButton
									onClick={() => {
										setUploadFileDialog(true);
									}}
									aria-label="add"
									color="primary"
									className={classes.btnAdd}
								>
									<AddBoxIcon style={{ width: 35, height: 35 }} />
								</IconButton>
							</>
						</Grid2>
						<Dialog open={deleteOpen} aria-labelledby="responsive-dialog-title">
							<DialogTitle id="responsive-dialog-title">
								{"WARNING"}
							</DialogTitle>
							<DialogContent style={{ minWidth: 400 }}>
								<DialogContentText style={{ padding: 0, margin: 0 }}>
									{listAttachmentSelected.length > 0 ? (
										listAttachmentSelected.length === 1 ? (
											<p style={{ margin: "0" }}>
												Remove <b>{listAttachmentSelected[0].FileName}</b> from
												work order?
											</p>
										) : (
											<>
												<p style={{ margin: 0 }}> Remove:</p>
												{listAttachmentSelected.map((Attachment) => (
													<p
														key={Attachment.UploadFileId}
														style={{ margin: "5px 0" }}
													>
														<b>⁃ {Attachment.FileName}, </b>
													</p>
												))}
												from work order?
											</>
										)
									) : rowSelectedInAttachment ? (
										<p style={{ margin: "0" }}>
											Remove <b>{rowSelectedInAttachment.FileName}</b> from work
											order?
										</p>
									) : (
										<h1>Something went wrong</h1>
									)}
								</DialogContentText>
							</DialogContent>
							<DialogActions style={{ padding: "16px 24px" }}>
								<Button
									onClick={() => setDeleteOpen(false)}
									autoFocus
									color="inherit"
								>
									CANCEL
								</Button>
								<Button
									autoFocus
									color="secondary"
									onClick={() => {
										if (listAttachmentSelected.length > 0) {
											removeAttachment(listAttachmentSelected);
										} else if (rowSelectedInAttachment) {
											removeAttachment([rowSelectedInAttachment]);
										}
									}}
								>
									REMOVE
								</Button>
							</DialogActions>
						</Dialog>
						<AssetAddAttachmentDialog
							show={showLinkResourcesToAssetDialog}
							onHide={(shouldUpdate: boolean) => {
								setShowLinkResourcesToAssetDialog(false);
								if (shouldUpdate) {
									searchWorkOrderAttachment();
								}
								setNewUploadedAttachments([]);
							}}
							AssetId={AssetId as number}
							searchAssetResources={searchAssetResources}
							newUploadedAttachments={newUploadedAttachments}
							WorkOrderName={WorkOrderName}
							AssetName={AssetName}
						/>
						<PreviewerAttachment />
					</Grid2>
					{uploadFileDialog && (
						<Grid2
							container
							justifyContent="center"
							alignItems="center"
							style={{ minHeight: "300px", zIndex: "1000" }}
						>
							<Grid2>
								<UploadFilePage
									WorkOrderId={WorkOrderId as number}
									searchWorkOrderAttachment={searchWorkOrderAttachment}
									setRemoveAttachmentError={setRemoveAttachmentError}
									gridRef={gridApi}
									setListMessage={setListMessage}
									setShowLinkResourcesToAssetDialog={
										setShowLinkResourcesToAssetDialog
									}
									setNewUploadedAttachments={setNewUploadedAttachments}
								/>
							</Grid2>
						</Grid2>
					)}
					<ErrorModal error={error} onHide={() => setError("")} />
					<ErrorModal error={errorApi} onHide={() => setErrorApi("")} />
					<ErrorModal
						error={workOrderAttachmentsError}
						onHide={() => setWorkOrderAttachmentsError("")}
					/>
					<ErrorModal
						error={linkAttachmentError}
						onHide={() => setLinkAttachmentError("")}
					/>
					<ErrorModal
						error={removeAttachmentError}
						onHide={() => setRemoveAttachmentError("")}
					/>
					<ErrorModal error={listMessage} onHide={() => setListMessage("")} />
				</div>
			)}
		</>
	);
};
